/**
 * Clinician Review Center — grouped-by-patient triage of pending technique
 * videos. Reuses the existing <ClinicianVideos patientId=…/> grid + its
 * per-video "Review completed" button (which already deletes the storage
 * asset on the server). New surface only: a card list with Red/Amber/Green
 * priority + pending count, backed by GET /api/videos/pending-by-patient.
 */
import { useEffect, useState, useCallback } from "react";
import ClinicianLayout from "@/components/DashLayout";
import ClinicianVideos from "@/components/ClinicianVideos";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, VideoIcon, ChevronLeft, ShieldAlert, TriangleAlert, CheckCircle2 } from "lucide-react";

const PRIORITY_META = {
  red:   { icon: ShieldAlert,   fg: "text-red-400",    border: "border-red-500/50",    bg: "bg-red-500/8",    label: "RED"   },
  amber: { icon: TriangleAlert, fg: "text-amber-300",  border: "border-amber-400/40",  bg: "bg-amber-400/6",  label: "AMBER" },
  green: { icon: CheckCircle2,  fg: "text-emerald-300",border: "border-emerald-500/30",bg: "bg-emerald-500/6",label: "GREEN" },
};

export default function ClinicianReviewCenter() {
  const [rows, setRows] = useState(null);
  const [selected, setSelected] = useState(null); // {patient_id, name, priority}
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/videos/pending-by-patient");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Review Center");
      setRows([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const totalPending = (rows || []).reduce((n, r) => n + r.pending_count, 0);
  const grouped = { red: [], amber: [], green: [] };
  (rows || []).forEach((r) => grouped[r.priority]?.push(r));

  return (
    <ClinicianLayout>
      {!selected ? (
        <>
          <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
            <div>
              <div className="fl-label text-[color:var(--fl-orange)]">Review Center</div>
              <h1 className="fl-heading text-3xl md:text-4xl mt-2" data-testid="rc-heading">
                {totalPending} video{totalPending === 1 ? "" : "s"} awaiting review
              </h1>
              <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>
                Grouped by patient · triaged Red → Amber → Green
              </div>
            </div>
            <button className="fl-btn-ghost text-sm" onClick={load} data-testid="rc-refresh">
              <RefreshCw size={13}/> Refresh
            </button>
          </div>

          {rows === null && <div className="fl-card p-6 text-sm" style={{ color: "var(--fl-muted)" }}>Loading…</div>}
          {rows && rows.length === 0 && (
            <div className="fl-card p-10 text-center text-sm" style={{ color: "var(--fl-muted)" }}>
              Nothing pending — you're all caught up.
            </div>
          )}

          {["red", "amber", "green"].map((p) => grouped[p].length > 0 && (
            <section key={p} className="mb-8">
              <div className="fl-label mb-3 flex items-center gap-2">
                <span className={PRIORITY_META[p].fg}>{PRIORITY_META[p].label}</span>
                <span style={{ color: "var(--fl-muted-2)" }}>· {grouped[p].length}</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {grouped[p].map((r) => <PatientCard key={r.patient_id} row={r} onOpen={() => setSelected(r)} />)}
              </div>
            </section>
          ))}
        </>
      ) : (
        <>
          <button
            className="fl-btn-ghost text-sm mb-4"
            onClick={() => { setSelected(null); load(); }}
            data-testid="rc-back"
          >
            <ChevronLeft size={13}/> Back to Review Center
          </button>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <PriorityPill priority={selected.priority} />
            <div>
              <div className="fl-heading text-2xl">{selected.name}</div>
              <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>{selected.email}</div>
            </div>
          </div>
          {/* Reuse the existing grid + per-video "Review completed" (deletes storage). */}
          <ClinicianVideos patientId={selected.patient_id} />
        </>
      )}
    </ClinicianLayout>
  );
}

function PatientCard({ row, onOpen }) {
  const meta = PRIORITY_META[row.priority];
  const Icon = meta.icon;
  return (
    <button
      onClick={onOpen}
      data-testid={`rc-patient-${row.patient_id}`}
      className={`fl-card text-left p-4 transition-colors hover:border-[color:var(--fl-orange)]/40 ${meta.border} ${meta.bg}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${meta.fg}`}>
            <Icon size={11}/> {meta.label}
          </div>
          <div className="fl-heading text-base mt-1 truncate">{row.name}</div>
          <div className="text-[11px] truncate" style={{ color: "var(--fl-muted-2)" }}>{row.email}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="fl-heading text-2xl leading-none">{row.pending_count}</div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--fl-muted-2)" }}>pending</div>
        </div>
      </div>
      <div className="text-[11px] mt-3 inline-flex items-center gap-1" style={{ color: "var(--fl-muted-2)" }}>
        <VideoIcon size={11}/> latest {new Date(row.latest_upload_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
      </div>
    </button>
  );
}

function PriorityPill({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.green;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full border ${m.border} ${m.fg}`}>
      <Icon size={11}/> {m.label}
    </span>
  );
}
